import { Component } from 'js-component-framework';
import ElementCoordinates from 'element-coordinates';
import throttle from 'lodash.throttle';
import './stickyElement.scss';

/*
 * Component for an element that tracks the window position as the user scrolls
 * through its parent element.
 *
 * Expected config:
 *
 * options: {
 *   offsetElementSelector: '.headroom',
 *   classes: {
 *     beforeFirstBoundary: 'StickyElement--before-first-boundary',
 *     afterLastBoundary: 'StickyElement--after-last-boundary',
 *   },
 * },
 */
export default class StickyElement extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    // Initiate the component
    super(config);

    // Check to make sure listener has not been applied before.
    if (this.element.classList.contains('StickyElement')) {
      return;
    }

    // Set up default options for config
    this.options = {
      offsetElementSelector: '',
      padding: 20,
      classes: {
        beforeFirstBoundary: 'StickyElement--before-first-boundary',
        afterLastBoundary: 'StickyElement--after-last-boundary',
      },
      ...this.options,
    };

    // Add marker to check for listener again.
    this.element.classList.add('StickyElement');

    // Initialize necessary styles
    this.element.parentNode.classList.add('StickyElement--parent');

    // Add elementTrack for calculating original element position
    this.createElementTrack();

    // Initialize variables
    this.calculateBoundaries();

    /**
     * Needs to apply a class if the parent bottom is farther up the window
     * than befre
     */
    window.addEventListener('scroll', throttle(() => {
      this.calculateBoundaries();
      const { beforeFirstBoundary, afterLastBoundary } = this.options.classes;

      // Do nothing if the element does not need to scroll.
      if (this.trackHeight === this.elHeight) {
        return;
      }

      if (this.beforeFirstBoundary()) {
        this.element.classList.add(beforeFirstBoundary);
      } else {
        this.element.classList.remove(beforeFirstBoundary);
      }

      /**
       * Set inline styles if element is between the boundaries.
       * These must be inline styles as top is computed based off offsetHeight.
       */
      if (this.betweenBoundaries()) {
        this.element.style.top = `${this.offsetHeight}px`;
        this.element.style.position = 'fixed';
        this.element.style.width = `${this.elWidth}px`;
        this.elementTrack.style.width = `${this.elWidth}px`;
      } else {
        this.element.style.top = '';
        this.element.style.width = '';
        this.element.style.position = '';
      }

      if (this.afterLastBoundary()) {
        this.element.classList.add(afterLastBoundary);
      } else {
        this.element.classList.remove(afterLastBoundary);
      }
    }, 100));
  }

  /**
   * Gets the unitless value of the height of the offset element.
   * @return {number} Height of element if found, or 0.
   */
  getOffsetHeight() {
    const { offsetElementSelector } = this.options;
    if (!offsetElementSelector) {
      return 0;
    }

    const offsetElement = document.querySelector(offsetElementSelector);
    if (!offsetElement) {
      return 0;
    }

    const offsetElementCoordinates = new ElementCoordinates(offsetElement)
      .borderBox;
    return offsetElementCoordinates ? offsetElementCoordinates.height : 0;
  }

  /**
   * Creates a div wrapper for the element which is used to apply flex styles
   * as well as calculate the top fo the element, even after the element moves
   * with its fixed position style down the page.
   */
  createElementTrack() {
    const { element } = this;
    const parent = element.parentNode;

    // Create element
    const elementTrack = document.createElement('div');
    elementTrack.classList.add('StickyElement--track');

    // Append track to parent.
    // This will intentionally move the element to the end of the dom order for
    // the parent.
    parent.appendChild(elementTrack);

    // Relocate element inside elementTrack.
    elementTrack.appendChild(element);

    // Add ref to track as property to this class.
    this.elementTrack = elementTrack;
  }

  /**
   * Calculates values necessary to determine if the element is within the
   * boundaries. Updates object parameters.
   */
  calculateBoundaries() {
    const { element, elementTrack } = this;
    const parent = elementTrack.parentNode;

    // Create coordinates objects in order to calculate values.
    const parentCoordinates = new ElementCoordinates(parent)
      .borderBox;
    const elementCoordinates = new ElementCoordinates(element).borderBox;
    const elementTrackCoordinates = elementTrack
      ? new ElementCoordinates(elementTrack).borderBox : elementCoordinates;

    // Width of the element
    this.elWidth = parentCoordinates.width;

    // Height of the element
    this.elHeight = elementCoordinates.height;

    // Location of the top of the element Track
    this.elTop = elementTrackCoordinates.top;

    // Length of pixels that element should be offset from the top
    this.offsetHeight = this.getOffsetHeight() + this.options.padding;

    // Location of the top of the parent's border
    this.parentTop = parentCoordinates.top;

    // Location of the bottom of the parent's border
    this.parentBottom = parentCoordinates.bottom;

    // Height of elementTrack element
    this.trackHeight = elementTrackCoordinates.height;

    // Use element top if it is lower than the parent, otherwise use the parent top.
    const firstBoundary = Math.max(this.elTop, this.parentTop);

    // Boundaries within which the element will move
    this.boundaries = [
      firstBoundary - this.offsetHeight,
      this.parentBottom - this.elHeight - this.offsetHeight,
    ];
  }

  /**
   * Checks if the scroll position is within the boundaries.
   * @return {bool}
   */
  betweenBoundaries() {
    const [firstBoundary, lastBoundary] = this.boundaries;
    return window.scrollY > firstBoundary && window.scrollY < lastBoundary;
  }

  /**
   * Checks if the first boundary has been passed.
   * @return {bool}
   */
  beforeFirstBoundary() {
    const [firstBoundary] = this.boundaries;
    return undefined === firstBoundary ? true : window.scrollY < firstBoundary;
  }

  /**
   * Checks if the last boundary has been passed.
   * @return {bool}
   */
  afterLastBoundary() {
    const [, lastBoundary] = this.boundaries;
    return undefined === lastBoundary ? false : window.scrollY > lastBoundary;
  }
}
